/**
 * <store-locator>
 * Section + Blocks driven store locator. Each enabled "store" block is emitted
 * both as a semantic list card (server-rendered) and as an entry in a JSON
 * payload consumed here. Map is Leaflet + OpenStreetMap; geocoding is Nominatim;
 * routing is a direct OSRM request. No metaobjects, no Google, no globals.
 *
 * Re-initialisation in the Theme Editor is handled by the custom-element
 * lifecycle: shopify:section:load inserts a fresh <store-locator> (connectedCallback
 * runs) and removes the old one (disconnectedCallback tears the map down).
 */
if (!customElements.get('store-locator')) {
	const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
	const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
	const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
	const OSRM_URL = 'https://router.project-osrm.org/route/v1/driving/';
	const EARTH_RADIUS = { mi: 3958.7558657441, km: 6371.0088 };

	const PIN_SVG =
		'<svg xmlns="http://www.w3.org/2000/svg" width="30" height="38" viewBox="0 0 30 38" fill="currentColor" aria-hidden="true">' +
		'<path d="M15 0C6.716 0 0 6.716 0 15c0 10.5 15 23 15 23s15-12.5 15-23C30 6.716 23.284 0 15 0Z"/>' +
		'<circle cx="15" cy="15" r="5.4" fill="#fff"/></svg>';

	function haversine(lat1, lng1, lat2, lng2, unit) {
		const r = EARTH_RADIUS[unit] || EARTH_RADIUS.mi;
		const toRad = (n) => (n * Math.PI) / 180;
		const dLat = toRad(lat2 - lat1);
		const dLng = toRad(lng2 - lng1);
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
		return r * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	}

	function debounce(fn, wait) {
		let t;
		return function debounced(...args) {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}

	class StoreLocator extends HTMLElement {
		connectedCallback() {
			if (this.initialised) return;
			this.initialised = true;

			this.unit = this.dataset.distanceUnit === 'km' ? 'km' : 'mi';
			this.defaultZoom = parseInt(this.dataset.defaultZoom, 10) || 11;
			const bp = parseInt(this.dataset.mobileBreakpoint, 10) || 750;
			this.mobileQuery = window.matchMedia(`(max-width: ${bp - 1}px)`);

			this.map = null;
			this.markers = new Map();
			this.originMarker = null;
			this.routeLayer = null;
			this.origin = null;
			this.activeId = null;
			this.pendingRouteId = null;
			this.searchId = 0;
			this.resultIds = null;
			this.currentView = 'list';
			this.handlers = {};

			this.parseStores();
			this.cacheDom();
			if (!this.stores.length) return;

			this.bindEvents();
			// Desktop shows both panes and applyView() builds the map now; on mobile
			// the map is built lazily the first time the Map tab is opened.
			this.applyView();
		}

		disconnectedCallback() {
			this.teardown();
		}

		/* ---- data ---------------------------------------------------------- */
		parseStores() {
			const node = this.querySelector('[data-store-locator-data]');
			let raw = [];
			try {
				raw = JSON.parse(node.textContent) || [];
			} catch (error) {
				console.error('[store-locator] could not parse store data', error);
			}
			this.stores = raw.map((store, index) => {
				const lat = parseFloat(store.latitude);
				const lng = parseFloat(store.longitude);
				return {
					...store,
					order: index,
					lat: Number.isFinite(lat) ? lat : null,
					lng: Number.isFinite(lng) ? lng : null,
					distance: null,
				};
			});
			this.storeById = new Map(this.stores.map((store) => [String(store.id), store]));
		}

		cacheDom() {
			this.els = {
				list: this.querySelector('[data-store-locator-list]'),
				mapEl: this.querySelector('[data-store-locator-map]'),
				status: this.querySelector('[data-store-locator-status]'),
				bar: this.querySelector('[data-store-locator-bar]'),
				searchForm: this.querySelector('[data-store-locator-search]'),
				searchInput: this.querySelector('[data-store-locator-search-input]'),
				locateBtn: this.querySelector('[data-store-locator-locate]'),
				tabs: Array.from(this.querySelectorAll('[data-store-locator-tab]')),
				panelList: this.querySelector('[data-store-locator-panel="list"]'),
				panelMap: this.querySelector('[data-store-locator-panel="map"]'),
			};
			this.cards = new Map();
			this.querySelectorAll('[data-store-locator-card]').forEach((card) => {
				this.cards.set(card.dataset.storeId, card);
			});
		}

		/* ---- events ------------------------------------------------------- */
		bindEvents() {
			if (this.els.list) {
				this.handlers.listClick = (event) => this.onListClick(event);
				this.els.list.addEventListener('click', this.handlers.listClick);
			}

			if (this.els.searchForm) {
				this.handlers.submit = (event) => {
					event.preventDefault();
					this.search(this.els.searchInput.value);
				};
				this.els.searchForm.addEventListener('submit', this.handlers.submit);

				const runInputSearch = debounce(() => {
					const value = this.els.searchInput.value.trim();
					if (!value) {
						this.clearSearch();
					} else if (value.length >= 3) {
						this.search(value);
					}
				}, 400);
				this.handlers.input = () => {
					this.searchId += 1;
					runInputSearch();
				};
				this.els.searchInput.addEventListener('input', this.handlers.input);
			}

			if (this.els.locateBtn) {
				this.handlers.locate = () => this.useMyLocation();
				this.els.locateBtn.addEventListener('click', this.handlers.locate);
			}

			this.handlers.tab = (event) => this.setView(event.currentTarget.dataset.storeLocatorTab, true);
			this.els.tabs.forEach((tab) => tab.addEventListener('click', this.handlers.tab));

			this.handlers.mq = () => this.applyView();
			this.mobileQuery.addEventListener('change', this.handlers.mq);

			// Theme editor: focus the card for the block being edited. The event target
			// is the element carrying {{ block.shopify_attributes }} (the <li>), so the
			// card is found by descending, with the block id as a fallback.
			this.handlers.blockSelect = (event) => {
				if (!this.contains(event.target)) return;
				const card =
					(event.target.matches('[data-store-locator-card]') && event.target) ||
					event.target.querySelector('[data-store-locator-card]') ||
					this.cards.get(event.detail && event.detail.blockId);
				if (card) this.selectStore(card.dataset.storeId, { fromEditor: true });
			};
			document.addEventListener('shopify:block:select', this.handlers.blockSelect);
		}

		teardown() {
			if (this.els && this.els.list && this.handlers.listClick) {
				this.els.list.removeEventListener('click', this.handlers.listClick);
			}
			if (this.mobileQuery && this.handlers.mq) {
				this.mobileQuery.removeEventListener('change', this.handlers.mq);
			}
			if (this.handlers.blockSelect) {
				document.removeEventListener('shopify:block:select', this.handlers.blockSelect);
			}
			if (this.map) {
				this.map.remove();
				this.map = null;
			}
		}

		/* ---- map --------------------------------------------------------- */
		whenLeafletReady(callback, attempt = 0) {
			if (window.L) {
				callback(window.L);
			} else if (attempt < 40) {
				setTimeout(() => this.whenLeafletReady(callback, attempt + 1), 100);
			} else {
				console.error('[store-locator] Leaflet failed to load');
			}
		}

		ensureMap(onReady) {
			if (this.map) {
				if (onReady) onReady(this.map);
				return;
			}
			if (!this.els.mapEl) return;
			this.whenLeafletReady((L) => {
				if (!this.isConnected) return;
				if (!this.map) {
					this.map = L.map(this.els.mapEl, {
						scrollWheelZoom: false,
						zoomControl: true,
					});
					L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(this.map);
					this.addMarkers(L);
					this.fitToStores();
					requestAnimationFrame(() => this.map && this.map.invalidateSize());
				}
				if (onReady) onReady(this.map);
			});
		}

		pinIcon(active) {
			return window.L.divIcon({
				className: 'store-locator__marker' + (active ? ' is-active' : ''),
				html: PIN_SVG,
				iconSize: [30, 38],
				iconAnchor: [15, 38],
				popupAnchor: [0, -34],
			});
		}

		addMarkers(L) {
			this.stores.forEach((store) => {
				if (store.lat === null || store.lng === null) return;
				const marker = L.marker([store.lat, store.lng], {
					icon: this.pinIcon(false),
					title: store.name,
					alt: store.name,
					keyboard: true,
				});
				marker.bindPopup(this.popupHtml(store), { className: 'store-locator__popup', minWidth: 200 });
				marker.on('click', () => this.selectStore(store.id, { fromMap: true }));
				if (!this.resultIds || this.resultIds.has(String(store.id))) marker.addTo(this.map);
				this.markers.set(String(store.id), marker);
			});
		}

		popupHtml(store) {
			const esc = (value) => this.escape(value);
			const name = esc(store.name);
			let html = store.store_page_url
				? `<a class="store-locator__popup-name" href="${esc(store.store_page_url)}">${name}</a>`
				: `<span class="store-locator__popup-name">${name}</span>`;
			if (store.address) html += `<p>${esc(store.address).replace(/\n/g, '<br>')}</p>`;
			html += '<p data-popup-distance hidden></p>';
			return html;
		}

		fitToStores(options = {}) {
			if (!this.map) return;
			const L = window.L;
			const points = [];
			this.stores.forEach((store) => {
				if (store.lat === null || store.lng === null) return;
				if (this.resultIds && !this.resultIds.has(String(store.id))) return;
				points.push([store.lat, store.lng]);
			});
			if (options.includeOrigin && this.origin) points.push([this.origin.lat, this.origin.lng]);

			if (!points.length) {
				this.map.setView([20, 0], 2);
			} else if (points.length === 1) {
				this.map.setView(points[0], this.defaultZoom);
			} else {
				this.map.fitBounds(L.latLngBounds(points).pad(0.15));
			}
		}

		/* ---- selection sync -------------------------------------------- */
		onListClick(event) {
			const directionsBtn = event.target.closest('[data-store-locator-directions]');
			if (directionsBtn) {
				const card = directionsBtn.closest('[data-store-locator-card]');
				this.routeTo(card.dataset.storeId);
				return;
			}

			if (event.target.closest('a[href]')) return;

			const card = event.target.closest('[data-store-locator-card]');
			if (card) this.selectStore(card.dataset.storeId, { fromList: true });
		}

		selectStore(id, options = {}) {
			const key = String(id);
			this.activeId = key;
			this.paintActive(key);

			if (this.mobileQuery.matches && options.fromList) this.setView('map');

			const focusMarker = () => {
				this.paintActive(key);
				const marker = this.markers.get(key);
				if (marker && this.map) {
					const zoom = Math.max(this.map.getZoom() || this.defaultZoom, 13);
					this.map.setView(marker.getLatLng(), zoom, { animate: !this.prefersReducedMotion() });
					marker.openPopup();
				}
			};

			if (options.fromMap) {
				focusMarker();
			} else {
				this.ensureMap(focusMarker);
			}

			const card = this.cards.get(key);
			if (card && options.fromMap) {
				card.scrollIntoView({ block: 'nearest', behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
			}
			if (card && (options.fromMap || options.fromEditor)) {
				card.focus({ preventScroll: true });
			}
		}

		paintActive(key) {
			this.cards.forEach((card, cardId) => {
				const on = cardId === key;
				card.classList.toggle('is-active', on);
				const btn = card.querySelector('[data-store-locator-select]');
				if (btn) btn.setAttribute('aria-pressed', String(on));
			});
			this.markers.forEach((marker, markerId) => {
				marker.setIcon(this.pinIcon(markerId === key));
			});
		}

		/* ---- search / geolocation ------------------------------------- */
		normalizeText(value) {
			return String(value || '')
				.toLowerCase()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.replace(/[^a-z0-9]+/g, ' ')
				.replace(/\s+/g, ' ')
				.trim();
		}

		cityFromAddress(address) {
			const lines = String(address || '')
				.split(/\n/)
				.map((line) => line.trim())
				.filter(Boolean);
			const cityLine = lines.length > 1 ? lines[1] : lines[0] || '';
			return this.normalizeText(cityLine.split(',')[0]);
		}

		queryTokens(query) {
			const generic = new Set([
				'street',
				'st',
				'road',
				'rd',
				'avenue',
				'ave',
				'boulevard',
				'blvd',
				'drive',
				'dr',
				'highway',
				'hwy',
				'unit',
				'east',
				'west',
				'north',
				'south',
				'canada',
				'the',
				'and',
			]);
			return this.normalizeText(query)
				.split(' ')
				.filter((token) => token && !generic.has(token));
		}

		matchStores(query) {
			const normalized = this.normalizeText(query);
			if (!normalized) return [];
			const tokens = this.queryTokens(query);
			const distinctive = tokens.length ? tokens : normalized.split(' ').filter(Boolean);

			return this.stores
				.map((store) => {
					const haystack = this.normalizeText(`${store.name || ''} ${store.address || ''}`);
					const words = new Set(haystack.split(' ').filter(Boolean));
					const city = this.cityFromAddress(store.address);
					let score = 0;

					if (haystack.includes(normalized)) score += 100;
					if (city && (city === normalized || (normalized.length >= 4 && city.startsWith(normalized)))) {
						score += 60;
					}

					const matched = distinctive.filter((token) => words.has(token));
					if (distinctive.length && matched.length === distinctive.length) score += 40 + matched.length;

					return { store, score };
				})
				.filter((entry) => entry.score > 0)
				.sort((a, b) => b.score - a.score || a.store.order - b.store.order)
				.map((entry) => entry.store);
		}

		showMatches(matches, options = {}) {
			if (!options.keepOrigin) {
				this.origin = null;
				if (this.originMarker) {
					this.originMarker.remove();
					this.originMarker = null;
				}
				this.clearRoute();
				this.stores.forEach((store) => {
					store.distance = null;
					this.renderDistance(store);
				});
			}

			this.resultIds = new Set(matches.map((store) => String(store.id)));
			this.applyResultVisibility();
			this.reorder(matches);

			if (!matches.length) {
				this.setStatus(this.dataset.noResultsText || 'No results found.');
				return;
			}

			this.setStatus('');
			if (matches.length === 1) {
				this.selectStore(matches[0].id, { fromSearch: true });
				return;
			}

			this.activeId = String(matches[0].id);
			this.paintActive(this.activeId);
			this.ensureMap(() => this.fitToStores());
		}

		applyResultVisibility() {
			this.cards.forEach((card, id) => {
				const item = card.closest('.store-locator__item');
				if (!item) return;
				item.hidden = Boolean(this.resultIds) && !this.resultIds.has(id);
			});
			if (!this.map) return;
			this.markers.forEach((marker, id) => {
				const visible = !this.resultIds || this.resultIds.has(id);
				const onMap = this.map.hasLayer(marker);
				if (visible && !onMap) marker.addTo(this.map);
				if (!visible && onMap) marker.remove();
			});
		}

		clearSearch() {
			this.searchId += 1;
			this.resultIds = null;
			this.applyResultVisibility();
			this.clearOrigin();
		}

		async geocodePlace(query) {
			const url = `${NOMINATIM_URL}?format=json&addressdetails=1&limit=5&countrycodes=ca&q=${encodeURIComponent(query)}`;
			const response = await fetch(url, { headers: { Accept: 'application/json' } });
			const results = await response.json();
			if (!Array.isArray(results) || !results.length) return null;
			return (
				results.find((item) => item.address && (item.address.country_code === 'ca' || !item.address.country_code)) ||
				results[0]
			);
		}

		async search(query) {
			query = (query || '').trim();
			const requestId = ++this.searchId;
			if (!query) {
				this.clearSearch();
				return;
			}

			const pendingId = this.pendingRouteId;
			if (pendingId) {
				this.setStatus(this.dataset.searchingText || 'Searching…');
				try {
					const place = await this.geocodePlace(query);
					if (requestId !== this.searchId) return;
					if (!place) {
						this.setStatus(this.dataset.noResultsText || 'No results found.');
						return;
					}
					this.resultIds = null;
					this.applyResultVisibility();
					this.pendingRouteId = pendingId;
					this.setOrigin(parseFloat(place.lat), parseFloat(place.lon), place.display_name);
				} catch (error) {
					if (requestId !== this.searchId) return;
					console.error('[store-locator] geocoding failed', error);
					this.setStatus(this.dataset.noResultsText || 'No results found.');
				}
				return;
			}

			const matches = this.matchStores(query);
			if (matches.length) {
				this.showMatches(matches);
				return;
			}

			this.setStatus(this.dataset.searchingText || 'Searching…');
			try {
				const place = await this.geocodePlace(query);
				if (requestId !== this.searchId) return;
				if (!place) {
					this.showMatches([]);
					return;
				}

				const address = place.address || {};
				const city = address.city || address.town || address.village || address.municipality || address.hamlet;
				if (city) {
					const cityMatches = this.matchStores(city);
					if (requestId !== this.searchId) return;
					if (cityMatches.length) {
						this.showMatches(cityMatches);
						return;
					}
				}

				this.setOrigin(parseFloat(place.lat), parseFloat(place.lon), place.display_name);
				if (requestId !== this.searchId) return;
				const nearest = this.stores
					.filter((store) => store.distance !== null)
					.sort((a, b) => a.distance - b.distance)[0];
				if (nearest && nearest.distance <= 25) {
					this.showMatches([nearest], { keepOrigin: true });
				} else {
					this.showMatches([], { keepOrigin: true });
				}
			} catch (error) {
				if (requestId !== this.searchId) return;
				console.error('[store-locator] geocoding failed', error);
				this.showMatches([]);
			}
		}

		useMyLocation() {
			if (!('geolocation' in navigator)) {
				this.setStatus(this.dataset.geoErrorText || 'Location is unavailable.');
				return;
			}
			this.setStatus(this.dataset.searchingText || 'Locating…');
			this.els.locateBtn.setAttribute('aria-busy', 'true');
			navigator.geolocation.getCurrentPosition(
				(position) => {
					this.els.locateBtn.removeAttribute('aria-busy');
					this.searchId += 1;
					this.resultIds = null;
					this.applyResultVisibility();
					if (this.els.searchInput) this.els.searchInput.value = '';
					this.setOrigin(position.coords.latitude, position.coords.longitude, this.dataset.yourLocationLabel || 'Your location');
					this.setStatus('');
				},
				(error) => {
					this.els.locateBtn.removeAttribute('aria-busy');
					console.warn('[store-locator] geolocation error', error);
					this.setStatus(this.dataset.geoErrorText || 'Location is unavailable.');
				},
				{ enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
			);
		}

		setOrigin(lat, lng, label) {
			if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
			this.origin = { lat, lng, label: label || '' };
			this.clearRoute();
			this.computeDistances();
			this.sortByDistance();

			this.ensureMap((map) => {
				if (!this.origin) return;
				const L = window.L;
				if (this.originMarker) this.originMarker.remove();
				this.originMarker = L.marker([this.origin.lat, this.origin.lng], {
					icon: L.divIcon({
						className: 'store-locator__marker store-locator__marker--origin',
						html: PIN_SVG,
						iconSize: [30, 38],
						iconAnchor: [15, 38],
						popupAnchor: [0, -34],
					}),
					title: this.origin.label,
					alt: this.origin.label,
				}).addTo(map);
				if (this.origin.label) {
					this.originMarker.bindPopup(
						`<span class="store-locator__popup-name">${this.escape(this.origin.label)}</span>`,
						{ className: 'store-locator__popup' }
					);
				}
				this.fitToStores({ includeOrigin: true });
			});

			const pendingId = this.pendingRouteId;
			this.clearOriginPrompt();
			if (pendingId) {
				this.routeTo(pendingId);
				return;
			}

			const nearest = this.stores
				.filter((store) => store.distance !== null)
				.sort((a, b) => a.distance - b.distance)[0];
			if (nearest) this.selectStore(nearest.id, { fromSearch: true });
		}

		clearOrigin() {
			this.origin = null;
			if (this.originMarker) {
				this.originMarker.remove();
				this.originMarker = null;
			}
			this.clearRoute();
			this.stores.forEach((store) => {
				store.distance = null;
				this.renderDistance(store);
			});
			this.restoreOrder();
			this.applyResultVisibility();
			this.setStatus('');
			this.fitToStores();
			if (this.pendingRouteId) this.promptForOrigin(this.pendingRouteId);
		}

		/* ---- distance ------------------------------------------------- */
		computeDistances() {
			if (!this.origin) return;
			this.stores.forEach((store) => {
				store.distance =
					store.lat !== null && store.lng !== null
						? haversine(this.origin.lat, this.origin.lng, store.lat, store.lng, this.unit)
						: null;
				this.renderDistance(store);
			});
		}

		renderDistance(store) {
			const card = this.cards.get(String(store.id));
			if (!card) return;
			const el = card.querySelector('[data-store-locator-distance]');
			if (!el) return;
			if (store.distance === null) {
				el.hidden = true;
				el.textContent = '';
				return;
			}
			const suffix = this.dataset.distanceSuffix ? ` ${this.dataset.distanceSuffix}` : '';
			el.hidden = false;
			el.textContent = `${store.distance.toFixed(1)} ${this.unit}${suffix}`;
		}

		sortByDistance() {
			this.reorder(
				[...this.stores].sort((a, b) => {
					if (a.distance === null && b.distance === null) return a.order - b.order;
					if (a.distance === null) return 1;
					if (b.distance === null) return -1;
					return a.distance - b.distance;
				})
			);
		}

		restoreOrder() {
			this.reorder([...this.stores].sort((a, b) => a.order - b.order));
		}

		reorder(ordered) {
			if (!this.els.list) return;
			const fragment = document.createDocumentFragment();
			ordered.forEach((store) => {
				const card = this.cards.get(String(store.id));
				const item = card && card.closest('.store-locator__item');
				if (item) fragment.appendChild(item);
			});
			this.els.list.appendChild(fragment);
		}

		/* ---- routing (OSRM) ----------------------------------------- */
		promptForOrigin(id) {
			this.pendingRouteId = String(id);
			this.selectStore(id, { fromList: true });
			this.setStatus(this.dataset.directionsHintText || 'Add your location above to get driving directions.');

			if (this.els.bar) {
				this.els.bar.classList.add('is-awaiting-origin');
				this.els.bar.scrollIntoView({
					block: 'center',
					behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
				});
			}

			if (this.els.searchInput) {
				this.els.searchInput.focus({ preventScroll: true });
			} else if (this.els.locateBtn) {
				this.els.locateBtn.focus({ preventScroll: true });
			}
		}

		clearOriginPrompt() {
			this.pendingRouteId = null;
			if (this.els.bar) this.els.bar.classList.remove('is-awaiting-origin');
		}

		async routeTo(id) {
			const store = this.storeById.get(String(id));
			if (!store) return;
			if (store.lat === null || store.lng === null) {
				this.setStatus(this.dataset.noResultsText || 'This store has no map location yet.');
				return;
			}

			this.selectStore(id, { fromList: true });

			if (!this.origin) {
				this.promptForOrigin(id);
				return;
			}

			this.clearOriginPrompt();
			if (this.mobileQuery.matches) this.setView('map');
			this.setStatus(this.dataset.searchingText || 'Calculating route…');

			const from = `${this.origin.lng},${this.origin.lat}`;
			const to = `${store.lng},${store.lat}`;
			try {
				const response = await fetch(`${OSRM_URL}${from};${to}?overview=full&geometries=geojson`);
				const data = await response.json();
				if (data.code !== 'Ok' || !data.routes || !data.routes.length) throw new Error('no route');
				const route = data.routes[0];
				this.drawRoute(route.geometry);
				const distance = this.unit === 'km' ? route.distance / 1000 : route.distance / 1609.344;
				const minutes = Math.round(route.duration / 60);
				this.setStatus(`${store.name} — ${distance.toFixed(1)} ${this.unit}, about ${minutes} min by car.`);
			} catch (error) {
				console.error('[store-locator] routing failed', error);
				const fallback = `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${this.origin.lat},${this.origin.lng};${store.lat},${store.lng}`;
				this.setStatusHtml(
					`${this.escape(this.dataset.noResultsText || 'Could not load the route.')} ` +
						`<a href="${fallback}" target="_blank" rel="noopener">OpenStreetMap</a>`
				);
			}
		}

		drawRoute(geometry) {
			this.whenLeafletReady((L) => {
				if (!this.map) return;
				this.clearRoute();
				this.routeLayer = L.geoJSON(geometry, {
					style: { className: 'store-locator__route', weight: 4, opacity: 0.85 },
				}).addTo(this.map);
				this.map.fitBounds(this.routeLayer.getBounds().pad(0.15));
			});
		}

		clearRoute() {
			if (this.routeLayer) {
				this.routeLayer.remove();
				this.routeLayer = null;
			}
		}

		/* ---- view (mobile tabs) ------------------------------------ */
		applyView() {
			if (this.mobileQuery.matches) {
				this.setView(this.currentView || 'list');
			} else {
				this.els.panelList.hidden = false;
				this.els.panelMap.hidden = false;
				this.els.tabs.forEach((tab) => {
					tab.setAttribute('aria-pressed', 'false');
					tab.classList.remove('is-active');
				});
				this.ensureMap();
				requestAnimationFrame(() => this.map && this.map.invalidateSize());
			}
		}

		setView(view, userInitiated) {
			this.currentView = view;
			if (this.mobileQuery.matches) {
				this.els.panelList.hidden = view !== 'list';
				this.els.panelMap.hidden = view !== 'map';
			} else {
				this.els.panelList.hidden = false;
				this.els.panelMap.hidden = false;
			}

			this.els.tabs.forEach((tab) => {
				const on = tab.dataset.storeLocatorTab === view;
				tab.setAttribute('aria-pressed', String(on));
				tab.classList.toggle('is-active', on);
			});

			if (view === 'map' || !this.mobileQuery.matches) {
				this.ensureMap();
				requestAnimationFrame(() => this.map && this.map.invalidateSize());
			}
			if (userInitiated && view === 'map' && this.els.mapEl) {
				this.els.mapEl.setAttribute('tabindex', '-1');
				this.els.mapEl.focus({ preventScroll: true });
			}
		}

		/* ---- status / util --------------------------------------- */
		setStatus(text) {
			if (!this.els.status) return;
			this.els.status.textContent = text || '';
			this.els.status.hidden = !text;
		}

		setStatusHtml(html) {
			if (!this.els.status) return;
			this.els.status.innerHTML = html;
			this.els.status.hidden = !html;
		}

		prefersReducedMotion() {
			return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		}

		escape(value) {
			return String(value == null ? '' : value)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;');
		}
	}

	customElements.define('store-locator', StoreLocator);
}
